/*#
# Public projects: a face anyone can see

A project's owner can make it **public**: its tile on the overview then
loads for anyone, signed in or not. Signed out, all you can do is look.

What a signed-out viewer reads is not the board's log. The log holds every
project and its whole history, and the host grants reading a collection
all or nothing. So each public project has **one summary document**, in a
collection anyone may read (`virta:public`): the project's name, icon,
colors, description, links and version, how many tasks are open, in
progress and blocked, when it last changed, and its latest showcases (each
one's title, words, link and picture; a stored picture is copied to
`virta:public-files`, which anyone may read). No other part of a task, no
body, comment, history or attachment is in it. One read per project is also the least a scraper
can cost.

- **Only a person signed in on the board makes a project public.** The
  `public` setting counts when the host stamped it as written in a
  session; an agent's token can turn it off, never on.
- **The summary is derived, never edited.** Any signed-in board or agent
  that sees it out of date rewrites it (`syncPublic`), and removes the
  summary of a project that is no longer public. A summary written from a
  later point in the log is never replaced by an older one.
- **What was public has been seen.** Turning it off removes the summary
  and its pictures; it can't recall what a viewer or a cache already has.

```
virta project publish        # bring the public summaries up to date (it also happens after any write)
```
*/

/*{ "parent": "Reference", "order": 45 }*/

import type { AttachedFile, ProjectProfile, Task } from './model'
import { isRetired } from './model'
import { pickShowcases } from './showcase'
import { isBlocked, PROJECT_KEY, tagValues } from './tags'

export const PUBLIC_COLLECTION = 'virta:public'
/** Where a public project's stored showcase pictures are copied, for anyone to load. */
export const PUBLIC_FILES_AREA = 'virta:public-files'
/** How many of a public project's latest showcases its summary carries. */
export const PUBLIC_SHOWCASES = 6

/** Whether a project's `public` setting says yes (the same spellings of "not" as `retired`). */
export function isPublic(p: { public?: string }): boolean {
  const v = String(p.public ?? '')
    .trim()
    .toLowerCase()
  return v !== '' && !['no', 'false', '0', 'off'].includes(v)
}

/**
 * A public project's showcase, as anyone reads it: what the carousel
 * shows, nothing more of the task (owner, 2026-10-14).
 */
export interface PublicShowcase {
  title: string
  text: string
  link: string
  alt: string
  /** the picture's own URL, when it isn't stored */
  media: string
  /** a stored picture: its path in `virta:public-files`, else '' */
  picture: string
  num: number
  at: string
}

/** What anyone may read about a public project: its tile and its showcases, nothing under them. */
export interface PublicProject {
  v: 1
  name: string
  icon: string
  color: string
  background: string
  description: string
  site: string
  package: string
  repo: string
  version: string
  retired: boolean
  open: number
  inProgress: number
  blocked: number
  /** when a task of it last changed (ISO), or '' */
  activity: string
  /** its latest showcases, newest first */
  showcases: PublicShowcase[]
  /** the log position this was written from: a later one is never replaced by an earlier */
  seq: number
}

/** What the summaries are made from (a store is one). */
export interface PublicBoard {
  tasks: Iterable<Task>
  projects: Iterable<ProjectProfile>
  cursor: number
}

/** The summary of one project, as the log stands. */
export function publicSummary(
  board: PublicBoard,
  p: ProjectProfile
): PublicProject {
  let open = 0
  let inProgress = 0
  let blocked = 0
  let activity = ''
  for (const t of board.tasks) {
    if (!tagValues(t.tags, PROJECT_KEY).includes(p.name)) continue
    if (t.updatedAt > activity) activity = t.updatedAt
    if (t.closedAt) continue
    open++
    if (tagValues(t.tags, 'status').includes('in-progress')) inProgress++
    if (isBlocked(t.tags)) blocked++
  }
  return {
    v: 1,
    name: p.name,
    icon: p.icon,
    color: p.color,
    background: p.background,
    description: p.description,
    site: p.site,
    package: p.package,
    repo: p.repo,
    version: p.version,
    retired: isRetired(p),
    open,
    inProgress,
    blocked,
    activity,
    showcases: pickShowcases(Array.from(board.tasks), {
      n: PUBLIC_SHOWCASES,
      project: p.name,
    }).map((t) => {
      const show = t.showcase!
      return {
        title: show.title,
        text: show.text,
        link: show.link,
        alt: show.alt,
        media: show.file ? '' : show.media,
        picture: show.file?.path ?? '',
        num: t.num,
        at: show.at,
      }
    }),
    seq: board.cursor,
  }
}

/** The stored pictures the public projects' showcases name, by path. */
export function publicPictures(board: PublicBoard): Map<string, AttachedFile> {
  const files = new Map<string, AttachedFile>()
  const names = new Set(
    Array.from(board.projects)
      .filter(isPublic)
      .map((p) => p.name)
  )
  for (const name of names)
    for (const t of pickShowcases(Array.from(board.tasks), {
      n: PUBLIC_SHOWCASES,
      project: name,
    }))
      if (t.showcase?.file) files.set(t.showcase.file.path, t.showcase.file)
  return files
}

/** Every public project's summary, by name. */
export function publicSummaries(board: PublicBoard): PublicProject[] {
  return Array.from(board.projects)
    .filter(isPublic)
    .map((p) => publicSummary(board, p))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** A summary's document id: the project's name, made safe for a path. */
export const publicDocId = (name: string): string =>
  name.replace(/[^A-Za-z0-9._-]/g, '_')

/** A stored document back as a summary, or null when it isn't one (the wire is untrusted). */
export function readPublic(doc: unknown): PublicProject | null {
  if (!doc || typeof doc !== 'object') return null
  const d = doc as Record<string, unknown>
  if (d.v !== 1 || typeof d.name !== 'string' || !d.name) return null
  const text = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '')
  const count = (k: string) =>
    typeof d[k] === 'number' && Number.isFinite(d[k]) && (d[k] as number) > 0
      ? Math.floor(d[k] as number)
      : 0
  return {
    v: 1,
    name: d.name,
    icon: text('icon'),
    color: text('color'),
    background: text('background'),
    description: text('description'),
    site: text('site'),
    package: text('package'),
    repo: text('repo'),
    version: text('version'),
    retired: d.retired === true,
    open: count('open'),
    inProgress: count('inProgress'),
    blocked: count('blocked'),
    activity: text('activity'),
    showcases: (Array.isArray(d.showcases) ? d.showcases : [])
      .slice(0, PUBLIC_SHOWCASES)
      .map(readShowcase)
      .filter((x): x is PublicShowcase => x !== null),
    seq: count('seq'),
  }
}

function readShowcase(doc: unknown): PublicShowcase | null {
  if (!doc || typeof doc !== 'object') return null
  const d = doc as Record<string, unknown>
  const text = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '')
  const picture = text('picture')
  // a picture path is the host's own `<sha256>.<ext>`: nothing that climbs
  if (picture && !/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(picture)) return null
  if (!picture && !text('media')) return null
  return {
    title: text('title'),
    text: text('text'),
    link: text('link'),
    alt: text('alt'),
    media: picture ? '' : text('media'),
    picture,
    num:
      typeof d.num === 'number' && Number.isInteger(d.num) && d.num > 0
        ? d.num
        : 0,
    at: text('at'),
  }
}

/** The part of the host client the summaries need. */
export interface PublicClient {
  listDocs(
    collection: string,
    options?: { count?: number }
  ): Promise<{ records: Record<string, unknown>[] }>
  /** `POST /docs`: every write in one transaction — all of them or none */
  commit(
    writes: { p: string; data: Record<string, unknown> }[]
  ): Promise<unknown>
  deleteDoc(path: string): Promise<void>
  getBlob(area: string, path: string): Promise<Blob | null>
  putBlob(
    area: string,
    path: string,
    bytes: Blob,
    contentType: string
  ): Promise<unknown>
  deleteBlob(area: string, path: string): Promise<void>
}

/** Every summary on a host: what a signed-out board shows. */
export async function fetchPublic(
  client: Pick<PublicClient, 'listDocs'>
): Promise<PublicProject[]> {
  const { records } = await client.listDocs(PUBLIC_COLLECTION, { count: 500 })
  return records
    .map(readPublic)
    .filter((p): p is PublicProject => p !== null)
    .sort((a, b) => a.name.localeCompare(b.name))
}

const same = (a: PublicProject, b: PublicProject): boolean =>
  JSON.stringify({ ...a, seq: 0 }) === JSON.stringify({ ...b, seq: 0 })

export interface PublicSync {
  /** summaries written, by project */
  published: string[]
  /** summaries removed: the project is no longer public */
  removed: string[]
  /** already right */
  unchanged: string[]
}

/**
 * Bring the host's summaries in line with the log: write what changed,
 * remove what is no longer public. A summary written from further along
 * the log than this board has read is left alone.
 */
export async function syncPublic(
  board: PublicBoard,
  client: PublicClient
): Promise<PublicSync> {
  const wanted = publicSummaries(board)
  const { records } = await client.listDocs(PUBLIC_COLLECTION, { count: 500 })
  const there = new Map<string, { path: string; summary: PublicProject }>()
  const strays: string[] = []
  for (const r of records) {
    const path = typeof r._path === 'string' ? r._path : ''
    const summary = readPublic(r)
    if (summary && path) there.set(summary.name, { path, summary })
    else if (path) strays.push(path)
  }
  const result: PublicSync = { published: [], removed: [], unchanged: [] }
  // the pictures first: a summary never names one that isn't there yet
  const pictures = publicPictures(board)
  const stored = new Set(
    (await client.listDocs(PUBLIC_FILES_AREA, { count: 500 })).records
      .map((r) => (typeof r.path === 'string' ? r.path : ''))
      .filter(Boolean)
  )
  for (const [path, file] of pictures) {
    if (stored.has(path)) continue
    const bytes = await client.getBlob(file.area, file.path)
    if (bytes) await client.putBlob(PUBLIC_FILES_AREA, path, bytes, file.type)
  }
  // one transaction: a board that is closed or put to sleep halfway (a phone
  // switching tabs) leaves every summary or none — never the first of
  // twelve (owner's iPhone, 2026-10-14)
  const writes: { p: string; data: Record<string, unknown> }[] = []
  for (const w of wanted) {
    const had = there.get(w.name)
    there.delete(w.name)
    if (had && (same(had.summary, w) || had.summary.seq > w.seq)) {
      result.unchanged.push(w.name)
      continue
    }
    writes.push({
      p: had?.path ?? `${PUBLIC_COLLECTION}/${publicDocId(w.name)}`,
      data: { ...w } as unknown as Record<string, unknown>,
    })
    result.published.push(w.name)
  }
  // the host takes at most 100 writes in one commit
  for (let i = 0; i < writes.length; i += 100)
    await client.commit(writes.slice(i, i + 100))
  // what is left is public no longer — unless a later log made it so
  for (const [name, { path, summary }] of there) {
    if (summary.seq > board.cursor) continue
    await client.deleteDoc(path)
    result.removed.push(name)
  }
  for (const path of strays) await client.deleteDoc(path)
  // a picture no public showcase names any more: taken down
  for (const path of stored)
    if (!pictures.has(path)) await client.deleteBlob(PUBLIC_FILES_AREA, path)
  return result
}
